import { SUBJECT_COLOURS, type Subject } from './brand';
import { FONT_DISPLAY } from './marketingStyles';

// DodoSkills brand mark (R3 "Light and bold", owner's pick 2026-10-03).
// DodoSkills is the umbrella brand; each subject (Maths now, English next)
// is a sub-brand that reuses the same mark and wordmark and only swaps the
// colours plus the subject tag -- so a new subject is a new set of colours
// in SUBJECT_COLOURS, never a new logo.
//
// The mark is a dodo's head in profile on a light tile: a big round dark
// head with a little tuft, one eye, and the dodo's hooked beak in amber,
// pointing forward (to the right -- "onwards"). The light tile stands out
// on the dark app headers and in a phone's dark mode, where the old dark
// tile got lost. The same artwork lives in public/favicon.svg and
// logos/export/.

export function DodoMark({
  size = 32,
  subject = 'maths',
  onDark = false,
  className,
}: {
  size?: number;
  subject?: Subject | 'umbrella';
  // For a dark header. The light tile already stands out there, so the
  // tile stays light; only the thin outline is dropped (it is there to
  // hold the tile's edge on a light page).
  onDark?: boolean;
  className?: string;
}) {
  const colours = SUBJECT_COLOURS[subject];
  const tile = colours.bird;
  const head = colours.tile;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      className={className}
    >
      <rect width="32" height="32" rx="8" fill={tile} />
      {!onDark && (
        <rect
          x="0.5"
          y="0.5"
          width="31"
          height="31"
          rx="7.5"
          fill="none"
          stroke={head}
          strokeOpacity="0.12"
        />
      )}
      <path
        d="M19.4 12.9c4.2-1.3 8.9.3 10.1 3.7.2.5.1 1-.1 1.5l-.9 2.6c-.2.5-.8.6-1.1.1-1.3-1.8-3.6-2-6-1.4Z"
        fill={colours.beak}
      />
      <path
        d="M11.9 9.8c-1-2.3-.5-4.5 1.9-4.9"
        fill="none"
        stroke={head}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12.8" cy="18.2" r="9.3" fill={head} />
      <circle cx="14.6" cy="15.2" r="2.1" fill={tile} />
      <circle cx="15.1" cy="15.2" r="0.95" fill={head} />
    </svg>
  );
}

// Mark + wordmark + subject tag, e.g. [mark] DodoSkills  MATHS
export function DodoLogo({
  subjectLabel = 'Maths',
}: {
  subjectLabel?: string;
}) {
  return (
    <span className="flex items-center gap-3">
      <DodoMark size={36} className="lg:size-12" />
      <span
        className={`${FONT_DISPLAY} text-[23px] leading-none font-bold whitespace-nowrap text-ink lg:text-[30px]`}
      >
        DodoSkills
      </span>
      {subjectLabel && (
        <span className="rounded-md bg-mint px-2 py-1 text-[11px] leading-none font-bold tracking-wider text-forest-800 uppercase lg:text-[12px]">
          {subjectLabel}
        </span>
      )}
    </span>
  );
}
